import datetime from 'lib/datetime';
import { DELIVERY_FEE_TIERS, FUEL_SURCHARGE } from './constants';
import { DeliveryFeeTier, DeliveryProgressInfo } from './types';
import config from 'appConfig';

const isValidOrderValue = (value: number): boolean =>
  typeof value === 'number' && !Number.isNaN(value) && value >= 0;

export const getCurrentTierIndex = (total: number): number => {
  if (!isValidOrderValue(total)) return -1;

  return DELIVERY_FEE_TIERS.findIndex(
    (t) => total >= t.min && total <= t.max,
  );
};

export const getNextTier = (
  total: number,
): DeliveryFeeTier | null => {
  const currentIndex = getCurrentTierIndex(total);

  if (currentIndex === -1) return null;

  if (currentIndex >= DELIVERY_FEE_TIERS.length - 1) {
    return null;
  }

  return DELIVERY_FEE_TIERS[currentIndex + 1] ?? null;
};

const getDeliveryFee = (tier: DeliveryFeeTier): number =>
  tier.fee + (tier.fee === 0 ? 0 : FUEL_SURCHARGE);

export const getDeliveryProgressInfo = (
  total: number,
): DeliveryProgressInfo => {
  const currentTierIndex = getCurrentTierIndex(total);

  if (currentTierIndex === -1) {
    return {
      amountAway: null,
      currentFee: null,
      nextFee: null,
    };
  }

  const currentTier = DELIVERY_FEE_TIERS[currentTierIndex];
  const currentFee = getDeliveryFee(currentTier);

  const nextTier = getNextTier(total);

  if (!nextTier) {
    return {
      amountAway: 0,
      currentFee,
      nextFee: 0,
    };
  }

  const amountAway = Math.max(nextTier.min - total, 0);

  return {
    amountAway: parseFloat(amountAway.toFixed(2)),
    currentFee,
    // TODO: update this when fuel surcharge is lifted
    nextFee: getDeliveryFee(nextTier),
  };
};

export const getDeliveryTierOverallProgressPercent = (
  total: number,
): number => {
  if (!isValidOrderValue(total)) return 0;

  const currentIndex = getCurrentTierIndex(total);

  if (currentIndex === -1) return 0;

  if (currentIndex === DELIVERY_FEE_TIERS.length - 1) return 100;

  const currentTier = DELIVERY_FEE_TIERS[currentIndex];
  const nextTier = DELIVERY_FEE_TIERS[currentIndex + 1];

  const basePercent =
    (currentIndex / (DELIVERY_FEE_TIERS.length - 1)) * 100;
  const nextPercent =
    ((currentIndex + 1) / (DELIVERY_FEE_TIERS.length - 1)) * 100;

  const rangeMin = currentTier.min;
  const rangeMax = nextTier.min;

  const progressWithinTier = Math.min(
    Math.max((total - rangeMin) / (rangeMax - rangeMin), 0),
    1,
  );

  const percent =
    basePercent + (nextPercent - basePercent) * progressWithinTier;

  return Math.round(percent);
};

export const getDeliveryTierProgressPercent = (total: number) => {
  if (!isValidOrderValue(total)) return 0;

  const currentIndex = getCurrentTierIndex(total);
  if (currentIndex === -1) return 0;

  if (currentIndex === DELIVERY_FEE_TIERS.length - 1) return 100;

  const currentTier = DELIVERY_FEE_TIERS[currentIndex];
  const nextTier = DELIVERY_FEE_TIERS[currentIndex + 1];

  const progress =
    (total - currentTier.min) / (nextTier.min - currentTier.min);

  return Math.round(Math.min(Math.max(progress, 0), 1) * 100);
};

export const isNewDeliveryFeeOccurred = (): boolean => {
  const now = datetime();

  return now.isAfter(
    datetime.tz(config.newDeliveryFeeStartDate, config.timezone),
  );
};
